/*
 * Copyright (c) 2022, Salesforce, Inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React, {Fragment, useCallback, useEffect, useRef, useState} from 'react';
import {useIntl} from 'react-intl';
import PropTypes from 'prop-types';

import {
  useCategory, useCustomerId, useShopperBasketsMutation, useShopperCustomersMutation,
  useAccessToken, useCommerceApi,
} from '@salesforce/commerce-sdk-react';
import {useProducts} from '@amblique/king-living/app/hooks/use-products';
import {HTTPError, HTTPNotFound} from '@salesforce/pwa-kit-react-sdk/ssr/universal/errors';
import {useServerContext} from '@salesforce/pwa-kit-react-sdk/ssr/universal/hooks';
import ProductView from '@amblique/king-living/app/components/product-view';
import {Box, Button, Stack} from '@salesforce/retail-react-app/app/components/shared/ui';
import {
  API_ERROR_MESSAGE, MAX_CACHE_AGE, STALE_WHILE_REVALIDATE, TOAST_ACTION_VIEW_WISHLIST,
  TOAST_MESSAGE_ADDED_TO_WISHLIST, TOAST_MESSAGE_ALREADY_IN_WISHLIST,
} from '@salesforce/retail-react-app/app/constants';
import useActiveData from '@salesforce/retail-react-app/app/hooks/use-active-data';
import {useCurrentCustomer} from '@salesforce/retail-react-app/app/hooks/use-current-customer';
import {useCurrentBasket} from '@salesforce/retail-react-app/app/hooks/use-current-basket';
import useDataCloud from '@salesforce/retail-react-app/app/hooks/use-datacloud';
import useEinstein from '@salesforce/retail-react-app/app/hooks/use-einstein';
import useNavigation from '@salesforce/retail-react-app/app/hooks/use-navigation';
import {useToast} from '@salesforce/retail-react-app/app/hooks/use-toast';
import {useWishList} from '@salesforce/retail-react-app/app/hooks/use-wish-list';
import InformationAccordion from '@amblique/king-living/app/pages/product-detail/partials/information-accordion';
import logger from '@salesforce/retail-react-app/app/utils/logger-instance';
import {getUpdateBundleChildArray, normalizeSetBundleProduct} from '@salesforce/retail-react-app/app/utils/product-utils';

import {useQueryClient} from '@tanstack/react-query';

import {useAddItemToNewOrExistingBasket} from '@amblique/king-living/app/hooks/use-add-item-to-new-or-existing-basket';
import {seedCartFlyoutDisplayFromLines} from '@amblique/king-living/app/utils/cart-flyout-display';
import {enqueueBasketMutation} from '@amblique/king-living/app/utils/basket-mutation-queue';
import {
  getNewBasketLineItems,
  rollbackNewCustomizeBasketLineItems,
} from '@amblique/king-living/app/utils/cart-utils';
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site';
import {useHeaderContext} from '@amblique/king-living/app/contexts/header-context';
import {CustomizeJourneyProvider} from '@amblique/king-living/app/context/customize-journey-context';
import {PdpWarehouseInventoryProvider} from '@amblique/king-living/app/context/pdp-warehouse-inventory-context';
import {useGlobalConfig} from '@amblique/king-living/app/contexts/global-config-context';
import {useBazaarvoiceScript} from '@amblique/king-living/app/hooks/use-bazaarvoice-script';
import {isBazaarvoiceConfigured} from '@amblique/king-living/app/utils/bazaarvoice';
import {useSitePreference} from '@amblique/base/app/hooks/use-site-preference';
import {
  basketLineLeadTimeFields,
  isSimpleProduct as checkIsSimpleProduct,
  openNotifyMeForm,
} from '@amblique/king-living/app/utils/product-utils';
import {
  trackPdpAddToCartFromCustomize,
  trackPdpAddToCartFromSelections,
  trackViewItem,
} from '@amblique/king-living/app/analytics/ga4';

export function ProductDetail({product, isProductLoading, isProductError, productError}) {
  const bazaarvoicePrefs = useSitePreference('bazaarvoice');

  useBazaarvoiceScript(isBazaarvoiceConfigured(bazaarvoicePrefs));
  const {formatMessage} = useIntl();
  const einstein = useEinstein();
  const dataCloud = useDataCloud();
  const activeData = useActiveData();
  const toast = useToast();
  const navigate = useNavigation();
  const customerId = useCustomerId();
  const {setHideOnScroll} = useHeaderContext();
  const {siteId} = useGlobalConfig();
  const {locale} = useMultiSite();
  const currency = locale?.preferredCurrency || 'AUD';
  const {data: customer} = useCurrentCustomer();
  const api = useCommerceApi();
  const {getTokenWhenReady} = useAccessToken();
  const lastViewItemProductIdRef = useRef(null);

  /** **************************** Basket *********************************/
  const {data: basket, isLoading: isBasketLoading} = useCurrentBasket();
  const queryClient = useQueryClient();
  const {addItemToNewOrExistingBasket} = useAddItemToNewOrExistingBasket();
  const addItemToBasketMutation = useShopperBasketsMutation('addItemToBasket');
  const removeItemFromBasketMutation = useShopperBasketsMutation('removeItemFromBasket');
  const updateItemInBasketMutation = useShopperBasketsMutation('updateItemInBasket');
  const updateItemsInBasketMutation = useShopperBasketsMutation('updateItemsInBasket');
  const {res} = useServerContext();

  if (res) {
    res.set(
      'Cache-Control',
      `s-maxage=${MAX_CACHE_AGE}, stale-while-revalidate=${STALE_WHILE_REVALIDATE}`
    );
  }

  useEffect(() => {
    setHideOnScroll(true);

    return () => setHideOnScroll(false);
  }, [setHideOnScroll]);

  // Note: Since category needs id from product detail, it can't be server side rendered atm
  // until we can do dependent query on server
  const {
    data: category,
    isError: isCategoryError,
    error: categoryError,
    isFetched,
  } = useCategory({
    parameters: {
      id: product?.primaryCategoryId,
      levels: 1,
    },
  });

  /** **************************** Sets and Bundles *********************************/
  const [childProductSelection, setChildProductSelection] = useState({});
  const [childProductOrderability, setChildProductOrderability] = useState({});
  const [selectedBundleQuantity, setSelectedBundleQuantity] = useState(1);
  const childProductRefs = React.useRef({});
  const isProductASet = product?.type.set;
  const isProductABundle = product?.type.bundle;

  let bundleChildVariantIds = '';

  if (isProductABundle) bundleChildVariantIds = Object.keys(childProductSelection)
    ?.map((key) => childProductSelection[key].variant.productId)
    .join(',');

  const {data: bundleChildrenData} = useProducts(
    {
      parameters: {
        ids: bundleChildVariantIds,
        allImages: false,
        expand: ['availability', 'variations'],
        select: '(data.(id,inventory,master))',
      },
    },
    {
      enabled: bundleChildVariantIds?.length > 0,
      keepPreviousData: true,
    }
  );

  if (isProductABundle && bundleChildrenData) {
    // Loop through the bundle children and update the inventory for variant selection
    product.bundledProducts.forEach(({product: childProduct}, index) => {
      const matchingChildProduct = bundleChildrenData.data.find(
        (bundleChild) => bundleChild.master.masterId === childProduct.id
      );

      if (matchingChildProduct) {
        product.bundledProducts[index].product = {
          ...childProduct,
          inventory: matchingChildProduct.inventory,
        };
      }
    });
  }

  const comboProduct = isProductASet || isProductABundle ? normalizeSetBundleProduct(product) : {};

  /** ************** Error Handling ****************/

  if (isProductError) {
    const errorStatus = productError?.response?.status;

    switch (errorStatus) {
      case 404:
        throw new HTTPNotFound('Product Not Found.');
      default:
        throw new HTTPError(errorStatus, `HTTP Error ${errorStatus} occurred.`);
    }
  }
  if (isCategoryError) {
    const errorStatus = categoryError?.response?.status;

    switch (errorStatus) {
      case 404:
        throw new HTTPNotFound('Category Not Found.');
      default:
        throw new HTTPError(errorStatus, `HTTP Error ${errorStatus} occurred.`);
    }
  }

  const [primaryCategory, setPrimaryCategory] = useState(category);

  // This page uses the `primaryCategoryId` to retrieve the category data. This attribute
  // is only available on `master` products. Since a variation will be loaded once all the
  // attributes are selected (to get the correct inventory values), the category information
  // is overridden. This will allow us to keep the initial category around until a different
  // master product is loaded.
  useEffect(() => {
    if (category) {
      setPrimaryCategory(category);
    }
  }, [category]);

  /** ************** Product Variant ****************/
  // KINDW-1121: Simple master PDPs (including Freeform) preserve the master URL.
  // Variant-specific selections are tracked in CustomizeJourneyContext and the
  // SelectedVariant in SCAPI payloads — no URL rewrite is needed or desired.

  /** ************** Wishlist ****************/
  const {data: wishlist, isLoading: isWishlistLoading} = useWishList();
  const createCustomerProductListItem = useShopperCustomersMutation(
    'createCustomerProductListItem'
  );

  const handleAddToWishlist = (product, variant, quantity) => {
    const isItemInWishlist = wishlist?.customerProductListItems?.find(
      (i) => i.productId === variant?.productId || i.productId === product?.id
    );

    if (!isItemInWishlist) {
      createCustomerProductListItem.mutate(
        {
          parameters: {
            listId: wishlist.id,
            customerId,
          },
          body: {
            // NOTE: API does not respect quantity, it always adds 1
            quantity,
            productId: variant?.productId || product?.id,
            public: false,
            priority: 1,
            type: 'product',
          },
        },
        {
          onSuccess: () => {
            toast({
              title: formatMessage(TOAST_MESSAGE_ADDED_TO_WISHLIST, {quantity: 1}),
              status: 'success',
              action: (
                // it would be better if we could use <Button as={Link}>
                // but unfortunately the Link component is not compatible
                // with Chakra Toast, since the ToastManager is rendered via portal
                // and the toast doesn't have access to intl provider, which is a
                // requirement of the Link component.
                <Button
                  variant="link"
                  onClick={() => navigate('/account/wishlist')}
                >
                  {formatMessage(TOAST_ACTION_VIEW_WISHLIST)}
                </Button>
              ),
            });
          },
          onError: () => {
            showError();
          },
        }
      );
    } else {
      toast({
        title: formatMessage(TOAST_MESSAGE_ALREADY_IN_WISHLIST),
        status: 'info',
        action: (
          <Button variant="link" onClick={() => navigate('/account/wishlist')}>
            {formatMessage(TOAST_ACTION_VIEW_WISHLIST)}
          </Button>
        ),
      });
    }
  };

  /** ************** Notify Me ****************/
  const handleNotifyMeClick = useCallback((product) => {
    openNotifyMeForm(product);
  }, []);

  /** ************** Add To Cart ****************/
  const showToast = useToast();
  const showError = () => {
    showToast({
      title: formatMessage(API_ERROR_MESSAGE),
      status: 'error',
    });
  };

  const handleAddToCart = async (productSelectionValues) => {
    try {
      const productItems = productSelectionValues.map(({product: selectionProduct, variant, quantity}) => {
        // For type.item products the API returns no variants array, so variant is undefined.
        // Fall back to the product itself (matches upstream pwa-kit behaviour).
        const selectedProduct = variant || selectionProduct;

        return {
          productId: selectedProduct?.productId || selectedProduct?.id,
          price: selectedProduct?.price,
          quantity,
          ...basketLineLeadTimeFields(selectedProduct),
        };
      });

      await addItemToNewOrExistingBasket(productItems);

      einstein.sendAddToCart(productItems);

      void trackPdpAddToCartFromSelections({
        currency,
        customer,
        productSelectionValues,
      });

      // If the items were successfully added, set the return value to be used
      // by the add to cart modal.
      return productSelectionValues;
    } catch (error) {
      console.log('error', error);
      showError(error);
    }
  };

  /**
   * Sequential add-to-cart for complex (guided) products.
   *
   * 1. Add the primary item — set product (with KingGuard optionItems) or
   *    cover variant. Carries c_selectedProducts and c_selectedOptionsUI.
   * 2. Extract the newly created line item UUID from the basket response.
   * 3. Add child (recommendation) items with c_parentProductLineItemUUId
   *    pointing back to the primary line item.
   *
   * @param {Object} primaryItem - From buildPrimaryCartItem()
   * @param {Array<Object>} childItems - From buildChildCartItems()
   */
  const handleCustomizeAddToCart = async (
    primaryItem,
    childItems = [],
    {displayTotal, optionPriceMap} = {}
  ) => {
    const existingItemIds = new Set(
      basket?.productItems?.map((li) => li.itemId) ?? []
    );
    const removeLineItem = (params) => removeItemFromBasketMutation.mutateAsync(params);

    let lastBasket = null;
    let basketId = null;

    try {
      lastBasket = await addItemToNewOrExistingBasket([primaryItem]);
      basketId = lastBasket?.basketId;

      const newPrimaryLineItem = getNewBasketLineItems(lastBasket, existingItemIds).find(
        (li) => li.productId === primaryItem.productId
      );
      const parentLineItemUUID = newPrimaryLineItem?.itemId;

      if (childItems.length > 0 && !parentLineItemUUID) {
        if (basketId) {
          await rollbackNewCustomizeBasketLineItems(basketId, lastBasket, existingItemIds, removeLineItem);
        }

        showError();

        return;
      }

      let finalBasket = lastBasket;

      if (childItems.length > 0 && basketId) {
        const enrichedChildren = childItems.map((item) => ({
          ...item,
          ...(parentLineItemUUID ? {c_parentProductLineItemUUId: parentLineItemUUID} : {}),
        }));

        try {
          finalBasket = await enqueueBasketMutation(() => addItemToBasketMutation.mutateAsync({
            parameters: {basketId},
            body: enrichedChildren,
          }));
          lastBasket = finalBasket;
          seedCartFlyoutDisplayFromLines(queryClient, enrichedChildren);
          seedCartFlyoutDisplayFromLines(queryClient, finalBasket?.productItems);
        } catch (childError) {
          await rollbackNewCustomizeBasketLineItems(basketId, lastBasket, existingItemIds, removeLineItem);
          console.error('[handleCustomizeAddToCart] Child add failed', childError);
          showError();

          return;
        }
      }

      if (basketId && finalBasket?.productItems) {
        const priceMap = {};

        if (primaryItem.price > 0) priceMap[primaryItem.productId] = primaryItem.price;
        childItems.forEach((ci) => {
          if (ci.price > 0) priceMap[ci.productId] = ci.price;
        });

        const itemsNeedingPriceUpdate = finalBasket.productItems.filter((li) => {
          if (existingItemIds.has(li.itemId)) return false;
          const expectedPrice = priceMap[li.productId];

          return expectedPrice > 0 && (li.price == null || li.price === 0);
        });

        if (itemsNeedingPriceUpdate.length > 0) {
          for (const item of itemsNeedingPriceUpdate) {
            try {
              await updateItemInBasketMutation.mutateAsync({
                parameters: {basketId, itemId: item.itemId},
                body: {
                  productId: item.productId,
                  quantity: item.quantity ?? 1,
                  price: priceMap[item.productId],
                },
              });
            } catch (e) {
              console.error('[handleCustomizeAddToCart] Price update failed for', item.productId, e);
            }
          }
        }
      }

      const allItems = [primaryItem, ...childItems];

      einstein.sendAddToCart(allItems);

      void trackPdpAddToCartFromCustomize({
        api,
        childItems,
        currency,
        customer,
        displayTotal,
        getToken: getTokenWhenReady,
        masterProduct: product,
        optionPriceMap,
        primaryItem,
      });

      return allItems;
    } catch (error) {
      if (basketId && lastBasket) {
        try {
          await rollbackNewCustomizeBasketLineItems(basketId, lastBasket, existingItemIds, removeLineItem);
        } catch (rollbackError) {
          console.error('[handleCustomizeAddToCart] Rollback failed', rollbackError);
        }
      }

      console.error('[handleCustomizeAddToCart] failed', error);
      showError();
    }
  };

  /** ************** Product Set/Bundles Handlers ****************/
  const handleChildProductValidation = useCallback(() => {
    // Run validation for all child products. This will ensure the error
    // messages are shown.
    Object.values(childProductRefs.current).forEach(({validateOrderability}) => {
      validateOrderability({scrollErrorIntoView: false});
    });

    // Using ot state for which child products are selected, scroll to the first
    // one that isn't selected.
    const selectedProductIds = Object.keys(childProductSelection);
    const firstUnselectedProduct = comboProduct.childProducts.find(
      ({product: childProduct}) => !selectedProductIds.includes(childProduct.id)
    )?.product;

    if (firstUnselectedProduct) {
      // Get the reference to the product view and scroll to it.
      const {ref} = childProductRefs.current[firstUnselectedProduct.id];

      if (ref.scrollIntoView) {
        ref.scrollIntoView({
          behavior: 'smooth',
          block: 'end',
        });
      }

      return false;
    }

    return true;
  }, [product, childProductSelection]);

  /** ************** Product Set Handlers ****************/
  const handleProductSetAddToCart = () => {
    // Get all the selected products, and pass them to the addToCart handler which
    // accepts an array.
    const productSelectionValues = Object.values(childProductSelection);

    return handleAddToCart(productSelectionValues);
  };

  /** ************** Product Bundle Handlers ****************/
  // Top level bundle does not have variants
  const handleProductBundleAddToCart = async (variant, selectedQuantity) => {
    try {
      const childProductSelections = Object.values(childProductSelection);

      const productItems = [
        {
          productId: product.id,
          price: product.price,
          quantity: selectedQuantity,
          ...basketLineLeadTimeFields(product),
          // The add item endpoint in the shopper baskets API does not respect variant selections
          // for bundle children, so we have to make a follow up call to update the basket
          // with the chosen variant selections
          bundledProductItems: childProductSelections.map((child) => {
            return {
              productId: child.variant.productId,
              quantity: child.quantity,
            };
          }),
        }
      ];

      const res = await addItemToNewOrExistingBasket(productItems);

      const bundleChildMasterIds = childProductSelections.map((child) => {
        return child.product.id;
      });

      // since the returned data includes all products in basket
      // here we compare list of productIds in bundleProductItems of each productItem to filter out the
      // current bundle that was last added into cart
      const currentBundle = res.productItems.find((productItem) => {
        if (!productItem.bundledProductItems?.length) return; // eslint-disable-line array-callback-return
        const bundleChildIds = productItem.bundledProductItems?.map((item) => {
          // seek out the bundle child that still uses masterId as product id
          return item.productId;
        });

        return bundleChildIds.every((id) => bundleChildMasterIds.includes(id));
      });

      const itemsToBeUpdated = getUpdateBundleChildArray(
        currentBundle,
        childProductSelections
      );

      if (itemsToBeUpdated.length) {
        // make a follow up call to update child variant selection for product bundle
        // since add item endpoint doesn't currently consider product bundle child variants
        await updateItemsInBasketMutation.mutateAsync({
          method: 'PATCH',
          parameters: {
            basketId: res.basketId,
          },
          body: itemsToBeUpdated,
        });
      }

      einstein.sendAddToCart(productItems);

      return childProductSelections;
    } catch (error) {
      showError(error);
    }
  };

  /** ************** GA4 view_item ****************/
  useEffect(() => {
    if (!product?.id) {
      return;
    }

    const productCategoryId = product.primaryCategoryId;
    let categoryTree;

    if (productCategoryId) {
      if (!isFetched || category?.id !== productCategoryId) {
        return;
      }

      categoryTree = category?.parentCategoryTree;
    } else {
      categoryTree = primaryCategory?.parentCategoryTree;
    }

    if (lastViewItemProductIdRef.current === product.id) {
      return;
    }

    lastViewItemProductIdRef.current = product.id;

    void trackViewItem({
      categoryTree,
      currency,
      customer,
      product,
    });
  }, [product, category, isFetched, primaryCategory, currency, customer]);

  /** ************** Einstein ****************/
  useEffect(() => {
    if (!product?.id) {
      return;
    }

    if (product.type.set) {
      einstein.sendViewProduct(product);
      dataCloud.sendViewProduct(product);
      const childrenProducts = product.setProducts;

      childrenProducts.map((child) => { // eslint-disable-line array-callback-return
        try {
          einstein.sendViewProduct(child);
        } catch (err) {
          logger.error('Einstein sendViewProduct error', {
            namespace: 'ProductDetail.useEffect',
            additionalProperties: {error: err, child},
          });
        }
        activeData.sendViewProduct(category, child, 'detail');
        dataCloud.sendViewProduct(child);
      });
    } else if (product) {
      try {
        einstein.sendViewProduct(product);
      } catch (err) {
        logger.error('Einstein sendViewProduct error', {
          namespace: 'ProductDetail.useEffect',
          additionalProperties: {error: err, product},
        });
      }
      activeData.sendViewProduct(category, product, 'detail');
      dataCloud.sendViewProduct(product);
    }
  }, [product]);

  return (
    <Box
      className="sf-product-detail-page"
      data-testid="product-details-page"
      layerStyle="fullWidth"
    >
      <Stack spacing={{base: 15, lg: 16}}>
        {isProductASet || isProductABundle ? (
          <Fragment>
            <ProductView
              addToWishlist={handleAddToWishlist}
              category={primaryCategory?.parentCategoryTree || []}
              childProductOrderability={childProductOrderability}
              isBasketLoading={isBasketLoading}
              isProductLoading={isProductLoading}
              isWishlistLoading={isWishlistLoading}
              product={product}
              setSelectedBundleQuantity={setSelectedBundleQuantity}
              validateOrderability={handleChildProductValidation}
              addToCart={
                isProductASet ?
                  handleProductSetAddToCart :
                  handleProductBundleAddToCart
              }
            />

            <hr />

            {/* TODO: consider `childProduct.belongsToSet` */}
            {
              // Render the child products
              comboProduct.childProducts.map(
                ({product: childProduct, quantity: childQuantity}) => (
                  <Box key={childProduct.id} data-testid="child-product">
                    <ProductView
                      // Do not use an arrow function as we are manipulating the functions scope.
                      ref={function (ref) {
                        // Assign the "set" scope of the ref, this is how we access the internal
                        // validation.
                        childProductRefs.current[childProduct.id] = {
                          ref,
                          validateOrderability: this.validateOrderability,
                        };
                      }}
                      childOfBundleQuantity={childQuantity}
                      isBasketLoading={isBasketLoading}
                      isProductLoading={isProductLoading}
                      isProductPartOfBundle={isProductABundle}
                      isProductPartOfSet={isProductASet}
                      isWishlistLoading={isWishlistLoading}
                      product={childProduct}
                      selectedBundleParentQuantity={selectedBundleQuantity}
                      addToCart={
                        isProductASet ?
                          (variant, quantity) => handleAddToCart([
                            {
                              product: childProduct,
                              variant,
                              quantity,
                            }
                          ]) :
                          null
                      }
                      addToWishlist={
                        isProductASet ? handleAddToWishlist : null
                      }
                      setChildProductOrderability={
                        setChildProductOrderability
                      }
                      onVariantSelected={(product, variant, quantity) => {
                        if (quantity) {
                          setChildProductSelection((previousState) => ({
                            ...previousState,
                            [product.id]: {
                              product,
                              variant,
                              quantity: isProductABundle ?
                                childQuantity :
                                quantity,
                            },
                          }));
                        } else {
                          const selections = {...childProductSelection};

                          delete selections[product.id];
                          setChildProductSelection(selections);
                        }
                      }}
                    />
                    {checkIsSimpleProduct(childProduct) && (
                      <InformationAccordion
                        isSimpleProduct={checkIsSimpleProduct(childProduct)}
                        product={childProduct}
                      />
                    )}

                    <Box display={{base: 'none', lg: 'block'}}>
                      <hr />
                    </Box>
                  </Box>
                )
              )
            }
          </Fragment>
        ) : product ? (
          <Fragment>
            <PdpWarehouseInventoryProvider>
              <CustomizeJourneyProvider key={product.id ?? product.productId} product={product} siteId={siteId}>
                <ProductView
                  addCustomizeToCart={handleCustomizeAddToCart}
                  addToCart={(variant, quantity) => handleAddToCart([{product, variant, quantity}])}
                  addToWishlist={handleAddToWishlist}
                  category={primaryCategory?.parentCategoryTree || []}
                  isBasketLoading={isBasketLoading}
                  isProductLoading={isProductLoading}
                  isWishlistLoading={isWishlistLoading}
                  product={product}
                  onNotifyMeClick={handleNotifyMeClick}
                />
              </CustomizeJourneyProvider>
            </PdpWarehouseInventoryProvider>
          </Fragment>
        ) : null}
      </Stack>
    </Box>
  );
}

ProductDetail.getTemplateName = () => 'product-detail';

ProductDetail.propTypes = {
  /**
   * The current react router match object. (Provided internally)
   */
  match: PropTypes.object, // eslint-disable-line react/no-unused-prop-types

  product: PropTypes.object,
  isProductLoading: PropTypes.bool,
  isProductError: PropTypes.bool,
  productError: PropTypes.object,
};
